import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Save, X, ShieldCheck, ShieldOff, ShieldAlert } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { toast } from 'sonner'
import { useState } from 'react'

interface GlobalUser {
  id: string
  email: string
  name: string
  role: string
  status: string
  last_login_at: string | null
  created_at: string
  tenant_id: string
  tenant_name: string
  mfa_enabled: number
  mfa_enforced: number
}

interface TenantOption {
  id: string
  name: string
  slug: string
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  tenant_admin: 'Admin',
  tenant_manager: 'Gestor',
  tenant_viewer: 'Visor',
}

const filters: FilterDef[] = [
  { key: 'search', label: 'Buscar', type: 'search', placeholder: 'Buscar por nombre o email...' },
  {
    key: 'role', label: 'Rol', type: 'select',
    options: [
      { value: 'super_admin', label: 'Super Admin' },
      { value: 'tenant_admin', label: 'Admin' },
      { value: 'tenant_manager', label: 'Gestor' },
      { value: 'tenant_viewer', label: 'Visor' },
    ],
  },
  {
    key: 'status', label: 'Estado', type: 'select',
    options: [
      { value: 'active', label: 'Activo' },
      { value: 'inactive', label: 'Inactivo' },
    ],
  },
]

const emptyCreate = { email: '', name: '', password: '', role: 'tenant_admin', tenantId: '' }
const emptyEdit = { name: '', role: '', status: '', password: '' }

export function AdminUsersPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreate)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState(emptyEdit)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const { data: tenants } = useQuery({
    queryKey: ['admin', 'tenants-switcher'],
    queryFn: () => api.get<TenantOption[]>('/admin/tenants-switcher'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'users', page, filterValues],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: '25' })
      if (filterValues.search) params.set('search', filterValues.search)
      if (filterValues.role) params.set('role', filterValues.role)
      if (filterValues.status) params.set('status', filterValues.status)
      return apiFetchJson<{
        ok: boolean
        data: GlobalUser[]
        meta: { page: number; perPage: number; total: number; totalPages: number }
      }>(`/admin/users?${params}`)
    },
  })

  const createMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/tenants/${createForm.tenantId}/users`, {
        email: createForm.email,
        name: createForm.name,
        password: createForm.password,
        role: createForm.role,
      }),
    onSuccess: () => {
      toast.success('Usuario creado')
      invalidate()
      setShowCreate(false)
      setCreateForm(emptyCreate)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, tenantId, data }: { userId: string; tenantId: string; data: Record<string, unknown> }) =>
      api.put(`/admin/tenants/${tenantId}/users/${userId}`, data),
    onSuccess: () => {
      toast.success('Usuario actualizado')
      invalidate()
      setEditingId(null)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ userId, tenantId }: { userId: string; tenantId: string }) =>
      api.delete(`/admin/tenants/${tenantId}/users/${userId}`),
    onSuccess: () => {
      toast.success('Usuario eliminado')
      invalidate()
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const resetMfaMutation = useMutation({
    mutationFn: ({ userId, tenantId }: { userId: string; tenantId: string }) =>
      api.put(`/admin/tenants/${tenantId}/users/${userId}`, { resetMfa: true }),
    onSuccess: () => {
      toast.success('MFA reseteado. El usuario debera reconfigurar TOTP en su proximo login.')
      invalidate()
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const enforcementMutation = useMutation({
    mutationFn: ({
      userId,
      tenantId,
      enforced,
    }: {
      userId: string
      tenantId: string
      enforced: boolean
    }) =>
      api.put(`/admin/tenants/${tenantId}/users/${userId}/mfa-enforcement`, { enforced }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.enforced
          ? 'MFA marcado como obligatorio. El usuario debera configurarlo en su proximo login.'
          : 'Usuario eximido de MFA. Podra acceder sin TOTP.',
      )
      invalidate()
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const startEdit = (u: GlobalUser) => {
    setEditingId(u.id)
    setEditForm({ name: u.name, role: u.role, status: u.status, password: '' })
  }

  const saveEdit = (u: GlobalUser) => {
    const data: Record<string, unknown> = {}
    if (editForm.name !== u.name) data.name = editForm.name
    if (editForm.role !== u.role) data.role = editForm.role
    if (editForm.status !== u.status) data.status = editForm.status
    if (editForm.password) data.password = editForm.password
    if (Object.keys(data).length === 0) { setEditingId(null); return }
    updateMutation.mutate({ userId: u.id, tenantId: u.tenant_id, data })
  }

  const users = response?.data ?? []


  return (
    <div className="space-y-6">
      <PageHeader
        title="Usuarios"
        description="Gestion global de usuarios de la plataforma"
        actions={
          <Button onClick={() => setShowCreate(!showCreate)}>
            <Plus className="h-4 w-4" /> Nuevo usuario
          </Button>
        }
      />

      {/* Create form */}
      {showCreate && (
        <Card>
          <h3 className="mb-4 font-semibold" style={{ color: 'var(--color-text-primary)' }}>Nuevo usuario</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Cliente *</label>
              <select
                value={createForm.tenantId}
                onChange={(e) => setCreateForm((f) => ({ ...f, tenantId: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <option value="">Seleccionar cliente...</option>
                {(tenants ?? []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Nombre *</label>
              <input
                type="text" value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Email *</label>
              <input
                type="email" value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                placeholder="usuario@empresa.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Contrasena *</label>
              <input
                type="password" value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
                placeholder="Min. 8 caracteres"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Rol *</label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <option value="super_admin">Super Admin</option>
                <option value="tenant_admin">Admin</option>
                <option value="tenant_manager">Gestor</option>
                <option value="tenant_viewer">Visor</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!createForm.tenantId || !createForm.email || !createForm.name || !createForm.password}
            >
              Crear usuario
            </Button>
            <Button variant="secondary" onClick={() => { setShowCreate(false); setCreateForm(emptyCreate) }}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      <FilterBar
        filters={filters}
        values={filterValues}
        onChange={(k, v) => { setFilterValues((prev) => ({ ...prev, [k]: v })); setPage(1) }}
        onClear={() => { setFilterValues({}); setPage(1) }}
      />

      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                {['Nombre', 'Cliente', 'Rol', 'Estado', 'MFA', 'Ultimo acceso', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-4 py-4">
                      <div className="h-10 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                    Sin usuarios
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isEditing = editingId === u.id
                  return (
                    <tr key={u.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                      {/* Nombre */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text" value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            className="w-full rounded-md border px-2 py-1.5 text-sm"
                            style={{ borderColor: 'var(--color-border-focus)', minWidth: '160px' }}
                          />
                        ) : (
                          <div>
                            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{u.name}</p>
                            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{u.email}</p>
                          </div>
                        )}
                      </td>

                      {/* Cliente */}
                      <td className="px-4 py-3">
                        <a
                          href={`/admin/clients/${u.tenant_id}`}
                          className="text-sm font-medium hover:underline"
                          style={{ color: 'var(--color-brand-secondary)' }}
                        >
                          {u.tenant_name}
                        </a>
                      </td>

                      {/* Rol */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                            className="rounded-md border px-2 py-1.5 text-sm"
                            style={{ borderColor: 'var(--color-border-focus)' }}
                          >
                            <option value="super_admin">Super Admin</option>
                            <option value="tenant_admin">Admin</option>
                            <option value="tenant_manager">Gestor</option>
                            <option value="tenant_viewer">Visor</option>
                          </select>
                        ) : (
                          <span
                            className="inline-flex rounded-md px-2 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: u.role === 'super_admin' ? 'var(--color-brand-primary-50)' : 'var(--color-gray-100)',
                              color: u.role === 'super_admin' ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                            }}
                          >
                            {roleLabels[u.role] ?? u.role}
                          </span>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                            className="rounded-md border px-2 py-1.5 text-sm"
                            style={{ borderColor: 'var(--color-border-focus)' }}
                          >
                            <option value="active">Activo</option>
                            <option value="disabled">Deshabilitado</option>
                          </select>
                        ) : (
                          <StatusBadge status={u.status} />
                        )}
                      </td>

                      {/* MFA */}
                      <td className="px-4 py-3">
                        {u.mfa_enabled ? (
                          <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>
                            <ShieldCheck className="h-3.5 w-3.5" /> Activo
                          </span>
                        ) : u.mfa_enforced ? (
                          <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor: 'var(--color-warning-light)', color: 'var(--color-warning)' }}>
                            <ShieldAlert className="h-3.5 w-3.5" /> Pendiente
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor: 'var(--color-gray-100)', color: 'var(--color-text-tertiary)' }}>
                            <ShieldOff className="h-3.5 w-3.5" /> Exento
                          </span>
                        )}
                      </td>

                      {/* Ultimo acceso */}
                      <td className="px-4 py-3">
                        <span className="text-sm tabular-nums">{formatDateTime(u.last_login_at)}</span>
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="space-y-2">
                            <div>
                              <input
                                type="password" value={editForm.password}
                                onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                                placeholder="Nueva contrasena (dejar vacio para no cambiar)"
                                className="w-full rounded-md border px-2 py-1.5 text-xs"
                                style={{ borderColor: 'var(--color-border)', minWidth: '200px' }}
                              />
                            </div>
                            <div className="flex gap-1">
                              <Button variant="primary" size="sm" onClick={() => saveEdit(u)} loading={updateMutation.isPending}>
                                <Save className="h-3.5 w-3.5" /> Guardar
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button variant="secondary" size="sm" onClick={() => startEdit(u)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {u.mfa_enabled ? (
                              <Button
                                variant="ghost" size="sm"
                                title="Resetear MFA"
                                onClick={() => {
                                  if (confirm(`Resetear MFA de ${u.name}? El usuario perdera el acceso TOTP y debera configurarlo de nuevo.`))
                                    resetMfaMutation.mutate({ userId: u.id, tenantId: u.tenant_id })
                                }}
                              >
                                <ShieldAlert className="h-3.5 w-3.5" style={{ color: 'var(--color-warning)' }} />
                              </Button>
                            ) : null}
                            {u.mfa_enforced ? (
                              <Button
                                variant="ghost" size="sm"
                                title="Eximir de MFA"
                                onClick={() => {
                                  if (confirm(
                                    `Eximir a ${u.name} de MFA?\n\nEl usuario podra acceder sin TOTP. Si tenia MFA configurado, se borrara.\n\nEsta accion queda registrada en auditoria.`
                                  ))
                                    enforcementMutation.mutate({ userId: u.id, tenantId: u.tenant_id, enforced: false })
                                }}
                              >
                                <ShieldOff className="h-3.5 w-3.5" style={{ color: 'var(--color-text-tertiary)' }} />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost" size="sm"
                                title="Re-exigir MFA"
                                onClick={() => {
                                  if (confirm(
                                    `Volver a exigir MFA a ${u.name}?\n\nEn su proximo login sera obligado a configurar TOTP.`
                                  ))
                                    enforcementMutation.mutate({ userId: u.id, tenantId: u.tenant_id, enforced: true })
                                }}
                              >
                                <ShieldCheck className="h-3.5 w-3.5" style={{ color: 'var(--color-success)' }} />
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => {
                                if (confirm(`Eliminar usuario ${u.name}?`))
                                  deleteMutation.mutate({ userId: u.id, tenantId: u.tenant_id })
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--color-error)' }} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {(response?.meta?.totalPages ?? 1) > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                {response?.meta?.total ?? 0} usuarios
              </p>
              <div className="flex gap-1">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                  {page} / {response?.meta?.totalPages ?? 1}
                </span>
                <Button variant="secondary" size="sm" disabled={page >= (response?.meta?.totalPages ?? 1)} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
